/**
 * @component AutocompleteDropdownComponent
 *
 * Reusable autocomplete dropdown component that can be positioned near any input field.
 * Filters options based on user input and allows selection from a list.
 *
 * @inputs
 * - `options` (string[]): Array of available options to display
 * - `formGroup` (FormGroup): The parent form group
 * - `controlName` (string): The form control name to bind to
 * - `inputElement` (HTMLElement): The input element to position the dropdown near
 *
 * @outputs
 * - Automatically updates the form control value when an option is selected
 */

import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup } from '@angular/forms';

@Component({
  selector: 'app-autocomplete-dropdown',
  templateUrl: './autocomplete-dropdown.component.html',
  styleUrls: ['./autocomplete-dropdown.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class AutocompleteDropdownComponent implements OnInit, OnDestroy, OnChanges {
  /** Array of available options */
  @Input() options: string[] = [];

  /** The parent form group */
  @Input() formGroup!: FormGroup;

  /** The form control name */
  @Input() controlName!: string;

  /** The input element to position near */
  @Input() inputElement?: HTMLElement;

  /**
   * Most rows to render at once.
   *
   * Every row is an `ion-item`, so an uncapped list built from the employee
   * directory renders hundreds of components the moment the field is focused —
   * enough to lock the page up. The list scrolls at 200px tall anyway, so nothing
   * past the cap was reachable without typing to narrow it down.
   */
  @Input() maxResults: number = 25;

  /**
   * Whether the field this belongs to is currently focused.
   *
   * The list must never open on its own: the form emits `valueChanges` for
   * reasons that are not typing — `enable()`/`disable()` while setting up field
   * states does it — which otherwise pops both directory lists open the moment
   * the details modal is opened, each offering the value already in the field.
   *
   * Defaults to true so callers that don't track focus behave as they always did.
   */
  @Input()
  public set active(isActive: boolean) {
    this._active = isActive;
    if (!isActive) { this.closeDropdown(); }
  }
  public get active(): boolean { return this._active; }
  private _active: boolean = true;

  /** Filtered options based on user input */
  public filteredOptions: string[] = [];

  /** Whether to show the dropdown */
  public showDropdown: boolean = false;

  /** Dropdown position styles (not needed with relative positioning) */
  public dropdownStyle: any = {};

  constructor() {}

  ngOnInit() {
    // Listen for resize events to close dropdown
    window.addEventListener('resize', () => this.closeDropdown());

    // Subscribe to value changes
    const control = this.formGroup?.get(this.controlName);
    if (control) {
      control.valueChanges.subscribe((value) => {
        this.onInputChange(value);
      });
    }
  }

  ngOnDestroy() {
    window.removeEventListener('resize', () => this.closeDropdown());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('options' in changes) {
      this.filteredOptions = this.capped(this.options);
    }
  }

  /** The first {@link maxResults} of a list — see that field for why. */
  private capped(options: string[]): string[] {
    return (options || []).slice(0, this.maxResults);
  }

  /**
   * Handle input changes to filter autocomplete options
   */
  public onInputChange(value: string) {
    // console.log('Dropdown onInputChange called with value:', value);
    // console.log('Available options:', this.options);
    
    if (value && this.options && this.options.length > 0) {
      // Match every token rather than the raw string, so "bob smith" finds a
      // person whose entry orders or punctuates the words differently.
      const tokens = value.toLowerCase().split(/[^a-z0-9]+/i).filter(Boolean);
      this.filteredOptions = this.capped(
        this.options.filter(option => {
          const candidate = option.toLowerCase();
          return tokens.every(token => candidate.includes(token));
        })
      );
      this.showDropdown = this.active && this.filteredOptions.length > 0;
    } else if (!value) {
      this.filteredOptions = this.capped(this.options);
      this.showDropdown = false;
    } else {
      this.filteredOptions = [];
      this.showDropdown = false;
    }
  }

  /**
   * Handle input focus to show all options
   */
  public onInputFocus() {
    if (this.active && this.options && this.options.length > 0) {
      this.filteredOptions = this.capped(this.options);
      this.showDropdown = true;
    }
  }



  /**
   * Select an option from the dropdown
   */
  public selectOption(option: string) {
    const control = this.formGroup?.get(this.controlName);
    if (control) {
      control.setValue(option);
    }
    this.showDropdown = false;
  }

  /**
   * Close the dropdown
   */
  public closeDropdown() {
    this.showDropdown = false;
  }
}
